<script setup lang="ts">
import { computed } from 'vue'
import DOMPurify from 'dompurify'
import WikiText from '@/components/wikitext/WikiText.vue'
import SavedPagePrintPreview from '@/print/SavedPagePrintPreview.vue'
import type { StandardDetail } from '@/standards/standard_types'
import type { AnchorObjective } from './anchor_page_types'

const PANEL_CLASSES = 'rounded-xxs border border-gray-400 p-4'

type PrintedObjective = Pick<AnchorObjective, 'level' | 'title' | 'text'>

const props = withDefaults(defineProps<{
  keyStandard?: StandardDetail | null
  title: string
  annualText: string
  goalExampleText?: string
  objectives?: PrintedObjective[]
  studentName: string
}>(), {
  keyStandard: null,
  goalExampleText: '',
  objectives: () => []
})

const emit = defineEmits<{
  close: []
  print: []
}>()

const standardHtml = computed(() => DOMPurify.sanitize(props.keyStandard?.standard_text ?? ''))
</script>

<template>
  <SavedPagePrintPreview :student-name="studentName" @close="emit('close')" @print="emit('print')">
    <h1 class="mb-2.5 mt-5 text-display-lg font-medium leading-tight">{{ title }}</h1>

    <template v-if="keyStandard">
      <h2 class="mb-2.5 mt-5 text-text-xl font-bold" data-testid="printable-standard-set">{{ keyStandard.standard_set }}</h2>
      <div :class="PANEL_CLASSES" data-testid="printable-key-standard">
        <h3 class="mb-2.5 mt-5 text-text-md font-regular">
          <b>Standard {{ keyStandard.standard_short_ref }}</b>&nbsp;&nbsp; <i>{{ keyStandard.standard_title }}</i>
        </h3>
        <blockquote v-if="keyStandard.core_purpose" class="mb-2 border-l-4 border-gray-300 px-3 pb-1 pt-0.5 text-text-sm">
          <strong>Core Purpose</strong>
          <p>{{ keyStandard.core_purpose }}</p>
        </blockquote>
        <div v-html="standardHtml" />
      </div>
    </template>

    <h2 class="mb-2.5 mt-5 text-text-xl font-bold">Annual Target</h2>
    <div :class="PANEL_CLASSES" class="fs-exclude" data-testid="printable-annual-target">
      <WikiText :text="annualText" />
      <WikiText v-if="goalExampleText" :text="goalExampleText" />
    </div>

    <template v-if="objectives.length > 0">
      <h3 class="mb-2.5 mt-5 text-text-md font-regular">Quarterly Objectives</h3>
      <div
        v-for="(objective, index) in objectives"
        :key="index"
        :class="PANEL_CLASSES"
        class="fs-exclude mb-2.5"
        data-testid="printable-objective"
      >
        <p class="mb-0 font-bold">{{ objective.level }} Quarter: {{ objective.title }}</p>
        <WikiText :text="objective.text" />
      </div>
    </template>
  </SavedPagePrintPreview>
</template>
